/**
 * The pre-lesson anchor page (A6, E1–E3, C1, C7): student profile & private
 * notes, progress charts, weekly plan builder with per-item transposition
 * overrides (B2), warm-up sequence and recent takes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { midiToName } from '@solfo/shared';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import { api } from '../../lib/api.ts';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Select,
  Spinner,
  Tag,
  Textarea,
  cls,
  fmtDate,
  fmtDateTime,
} from '../../lib/ui.tsx';
import { RangeBand, Ring, Sparkline } from '../../components/charts.tsx';

const VOICE_TYPES = ['soprano', 'mezzo', 'alto', 'tenor', 'baritone', 'bass'] as const;
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface StudentData {
  userId: string;
  status: string;
  voiceType: string | null;
  rangeLowMidi: number | null;
  rangeHighMidi: number | null;
  goals: string;
  coachNotes?: string;
  recapShareEnabled: boolean;
  user: { id: string; name: string; email: string };
  rangeTests: { id: string; lowMidi: number; highMidi: number; createdAt: string }[];
}

interface StudentStats {
  completionRate: number | null;
  streak: number;
  lastActivity: string | null;
  recentTakes: { id: string; exerciseId: string; accuracyScore: number | null; status: string; createdAt: string }[];
  accuracyTrends: { exerciseId: string; exerciseName: string; points: { t: string; score: number }[] }[];
  rangeHistory: { date: string; lowMidi: number; highMidi: number }[];
}

interface Exercise {
  id: string;
  name: string;
  tags: string;
  level: string;
  kind: string;
  estDurationSec: number;
}

interface PlanItem {
  id: string;
  exerciseId: string;
  orderIndex: number;
  targetDays: string;
  frequencyPerWeek: number;
  rootMidi: number;
  tempoBpm: number;
  rootMidiOverride?: boolean;
}

interface Plan {
  id: string;
  kind: string;
  title: string;
  weekStart: string | null;
  items: PlanItem[];
}

interface Template {
  id: string;
  name: string;
  items: { id: string; exerciseId: string }[];
}

interface DraftItem {
  exerciseId: string;
  frequencyPerWeek: number;
  targetDays: string[];
}

export default function StudentDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const student = useQuery({
    queryKey: ['student', id],
    queryFn: () => api<StudentData>(`/api/students/${id}`),
    enabled: !!id,
  });
  const stats = useQuery({
    queryKey: ['student-stats', id],
    queryFn: () => api<StudentStats>(`/api/analytics/student/${id}`),
    enabled: !!id,
  });
  const plans = useQuery({
    queryKey: ['plans', id, 'weekly'],
    queryFn: () => api<Plan[]>(`/api/plans?studentId=${id}&kind=weekly`),
    enabled: !!id,
  });
  const warmup = useQuery({
    queryKey: ['plans', id, 'warmup'],
    queryFn: () => api<Plan[]>(`/api/plans?studentId=${id}&kind=warmup`),
    enabled: !!id,
  });
  const exercises = useQuery({
    queryKey: ['exercises'],
    queryFn: () => api<Exercise[]>('/api/exercises'),
  });
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<Template[]>('/api/templates'),
  });

  const exName = useMemo(() => {
    const map = new Map<string, string>();
    for (const ex of exercises.data ?? []) map.set(ex.id, exLabel(ex));
    return (exerciseId: string) => map.get(exerciseId) ?? 'Unknown exercise';
  }, [exercises.data]);

  const invalidatePlans = () => {
    void qc.invalidateQueries({ queryKey: ['plans', id] });
  };

  const patchStudent = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/students/${id}`, { method: 'PATCH', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['student', id] }),
  });
  const patchItem = useMutation({
    mutationFn: (v: { itemId: string; rootMidi: number }) =>
      api(`/api/plans/items/${v.itemId}`, { method: 'PATCH', body: { rootMidi: v.rootMidi } }),
    onSuccess: invalidatePlans,
  });

  if (student.isLoading) return <Spinner />;
  if (student.error) return <ErrorText error={student.error} />;
  if (!student.data) return null;
  const sd = student.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-bold">
            {sd.user.name}
            {sd.status === 'archived' && (
              <span className="ml-2 align-middle">
                <Tag>archived</Tag>
              </span>
            )}
          </h1>
          <p className="text-xs text-slate-500">{sd.user.email}</p>
        </div>
        <Link to={`/coach/messages/${id}`}>
          <Button variant="secondary">💬 Message</Button>
        </Link>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ProfileCard student={sd} patch={patchStudent} />
        <StatsCard stats={stats} />
      </div>

      {stats.data && stats.data.accuracyTrends.length > 0 && (
        <Card title="Accuracy per exercise · last 4 weeks">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {stats.data.accuracyTrends.map((trend) => (
              <div key={trend.exerciseId} className="rounded-lg border border-slate-100 p-2">
                <p className="mb-1 truncate text-xs font-medium text-slate-600">{trend.exerciseName}</p>
                <Sparkline
                  points={trend.points.map((p) => ({ x: new Date(p.t).getTime(), y: p.score }))}
                  yDomain={[0, 100]}
                  height={72}
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="Range development">
        <RangeBand history={stats.data?.rangeHistory ?? []} />
      </Card>

      <PlanSection
        studentId={id}
        plans={plans}
        templates={templates.data ?? []}
        exercises={exercises.data ?? []}
        exName={exName}
        patchItem={patchItem}
      />

      <WarmupCard studentId={id} warmup={warmup} exercises={exercises.data ?? []} exName={exName} />

      <Card title="Recent takes">
        {!stats.data || stats.data.recentTakes.length === 0 ? (
          <p className="text-xs text-slate-400">No submitted takes yet.</p>
        ) : (
          <div className="space-y-2">
            {stats.data.recentTakes.map((take) => (
              <div key={take.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-700">{exName(take.exerciseId)}</p>
                  <p className="text-xs text-slate-400">
                    {fmtDateTime(take.createdAt)} · {take.status}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {take.accuracyScore !== null && (
                    <Tag tone={take.accuracyScore >= 80 ? 'green' : take.accuracyScore >= 60 ? 'amber' : 'red'}>
                      {take.accuracyScore}
                    </Tag>
                  )}
                  <Link
                    to={`/coach/review/${take.id}`}
                    className="text-xs font-medium text-brand-700 hover:underline"
                  >
                    Review
                  </Link>
                  <Link
                    to={`/coach/compare?student=${id}&exercise=${take.exerciseId}&a=${take.id}`}
                    className="text-xs font-medium text-brand-700 hover:underline"
                  >
                    Compare
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── A6 · Profile & private notes ───────────────────────────────────────────

function ProfileCard({
  student,
  patch,
}: {
  student: StudentData;
  patch: ReturnType<typeof useMutation<unknown, Error, Record<string, unknown>>>;
}) {
  const [goals, setGoals] = useState(student.goals);
  const [notes, setNotes] = useState(student.coachNotes ?? '');
  useEffect(() => {
    setGoals(student.goals);
    setNotes(student.coachNotes ?? '');
  }, [student.goals, student.coachNotes]);

  const dirty = goals !== student.goals || notes !== (student.coachNotes ?? '');

  return (
    <Card
      title="Profile"
      actions={
        <Button
          variant={student.status === 'active' ? 'secondary' : 'primary'}
          onClick={() => patch.mutate({ status: student.status === 'active' ? 'archived' : 'active' })}
          disabled={patch.isPending}
        >
          {student.status === 'active' ? 'Archive' : 'Activate'}
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <span className="text-xs font-medium tracking-wide text-slate-400 uppercase">Range</span>
          {student.rangeLowMidi !== null && student.rangeHighMidi !== null ? (
            <Tag tone="brand">
              {midiToName(student.rangeLowMidi)} – {midiToName(student.rangeHighMidi)}
            </Tag>
          ) : (
            <span className="text-xs text-slate-400">no range test yet</span>
          )}
          {student.rangeTests.length > 0 && (
            <span className="text-xs text-slate-400">
              last tested {fmtDate(student.rangeTests[student.rangeTests.length - 1]?.createdAt)}
            </span>
          )}
        </div>

        <Field label="Voice type">
          <Select
            value={student.voiceType ?? ''}
            onChange={(e) => patch.mutate({ voiceType: e.target.value || null })}
          >
            <option value="">— not set —</option>
            {VOICE_TYPES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Goals">
          <Textarea rows={2} value={goals} onChange={(e) => setGoals(e.target.value)} />
        </Field>

        <Field label="Coach notes" hint="Private — never visible to the student.">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {dirty && (
          <Button onClick={() => patch.mutate({ goals, coachNotes: notes })} disabled={patch.isPending}>
            Save notes
          </Button>
        )}

        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={student.recapShareEnabled}
            onChange={(e) => patch.mutate({ recapShareEnabled: e.target.checked })}
          />
          Allow shareable quarter recap (E8)
        </label>

        <ErrorText error={patch.error} />
      </div>
    </Card>
  );
}

// ── E1 · Stat tiles ──────────────────────────────────────────────────────────

function StatsCard({ stats }: { stats: { isLoading: boolean; error: unknown; data: StudentStats | undefined } }) {
  if (stats.isLoading)
    return (
      <Card title="Progress">
        <Spinner />
      </Card>
    );
  return (
    <Card title="Progress · last 4 weeks">
      {stats.error ? (
        <ErrorText error={stats.error} />
      ) : !stats.data ? null : (
        <div className="flex flex-wrap items-center justify-around gap-4">
          <Ring value={stats.data.completionRate ?? 0} label="Plan completion" sub={stats.data.completionRate === null ? 'no plan yet' : undefined} />
          <div className="text-center">
            <p className="text-xl font-bold text-slate-900">🔥 {stats.data.streak}</p>
            <p className="text-xs font-medium text-slate-600">day streak</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-slate-900">{fmtDate(stats.data.lastActivity)}</p>
            <p className="text-xs font-medium text-slate-600">last activity</p>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── C1 · Weekly plan builder ─────────────────────────────────────────────────

function PlanSection({
  studentId,
  plans,
  templates,
  exercises,
  exName,
  patchItem,
}: {
  studentId: string;
  plans: { isLoading: boolean; error: unknown; data: Plan[] | undefined };
  templates: Template[];
  exercises: Exercise[];
  exName: (id: string) => string;
  patchItem: ReturnType<typeof useMutation<unknown, Error, { itemId: string; rootMidi: number }>>;
}) {
  const qc = useQueryClient();
  const [building, setBuilding] = useState(false);
  const [title, setTitle] = useState('');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<DraftItem[]>([]);
  const [templateId, setTemplateId] = useState('');

  const createPlan = useMutation({
    mutationFn: () =>
      api('/api/plans', {
        body: {
          studentIds: [studentId],
          title,
          items: draft.map((d) => ({
            exerciseId: d.exerciseId,
            frequencyPerWeek: d.frequencyPerWeek,
            targetDays: d.targetDays.join(','),
          })),
        },
      }),
    onSuccess: () => {
      setDraft([]);
      setTitle('');
      setBuilding(false);
      void qc.invalidateQueries({ queryKey: ['plans', studentId] });
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const applyTemplate = useMutation({
    mutationFn: () => api('/api/plans/from-template', { body: { templateId, studentIds: [studentId] } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plans', studentId] });
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const filtered = search.trim()
    ? exercises.filter((ex) => `${ex.name} ${exLabel(ex)}`.toLowerCase().includes(search.trim().toLowerCase()))
    : exercises;

  function move(i: number, dir: -1 | 1) {
    setDraft((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      const a = next[i];
      const b = next[j];
      if (!a || !b) return prev;
      next[i] = b;
      next[j] = a;
      return next;
    });
  }

  function updateDraft(i: number, upd: Partial<DraftItem>) {
    setDraft((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...upd } : d)));
  }

  const current = plans.data?.[0];
  const past = (plans.data ?? []).slice(1);

  return (
    <Card
      title="Weekly plan"
      actions={
        <Button variant={building ? 'secondary' : 'primary'} onClick={() => setBuilding((b) => !b)}>
          {building ? 'Cancel' : '+ New plan'}
        </Button>
      }
    >
      {building && (
        <div className="mb-4 space-y-3 rounded-lg border border-brand-200 bg-brand-50/40 p-3">
          <Field label="Plan title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Week 12 — legato focus" />
          </Field>

          {draft.length > 0 && (
            <div className="space-y-2">
              {draft.map((d, i) => (
                <div key={`${d.exerciseId}-${i}`} className="rounded-lg border border-slate-200 bg-white p-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="min-w-0 truncate text-sm font-medium text-slate-700">{exName(d.exerciseId)}</p>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        className="rounded px-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        className="rounded px-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                        onClick={() => move(i, 1)}
                        disabled={i === draft.length - 1}
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                      <button
                        className="rounded px-1.5 text-red-500 hover:bg-red-50"
                        onClick={() => setDraft((prev) => prev.filter((_, idx) => idx !== i))}
                        aria-label="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1 text-xs text-slate-500">
                      <Select
                        className="w-auto py-1"
                        value={d.frequencyPerWeek}
                        onChange={(e) => updateDraft(i, { frequencyPerWeek: Number(e.target.value) })}
                      >
                        {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                          <option key={n} value={n}>
                            {n}×
                          </option>
                        ))}
                      </Select>
                      / week
                    </label>
                    <div className="flex items-center gap-1">
                      {DAY_KEYS.map((day) => (
                        <button
                          key={day}
                          onClick={() =>
                            updateDraft(i, {
                              targetDays: d.targetDays.includes(day)
                                ? d.targetDays.filter((x) => x !== day)
                                : [...d.targetDays, day],
                            })
                          }
                          className={cls(
                            'rounded px-1.5 py-0.5 text-[11px] font-medium',
                            d.targetDays.includes(day)
                              ? 'bg-brand-600 text-white'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200',
                          )}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Field label="Add exercises">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the library…" />
          </Field>
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {filtered.slice(0, 30).map((ex) => (
              <div key={ex.id} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-1.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-700">{exLabel(ex)}</p>
                  <p className="text-[11px] text-slate-400">
                    {ex.level} · {Math.round(ex.estDurationSec / 60)} min
                  </p>
                </div>
                <Button
                  variant="ghost"
                  className="shrink-0 px-2 py-1"
                  onClick={() =>
                    setDraft((prev) => [...prev, { exerciseId: ex.id, frequencyPerWeek: 3, targetDays: [] }])
                  }
                >
                  + Add
                </Button>
              </div>
            ))}
          </div>

          <Button onClick={() => createPlan.mutate()} disabled={draft.length === 0 || createPlan.isPending}>
            Assign plan ({draft.length} exercise{draft.length === 1 ? '' : 's'})
          </Button>
          <ErrorText error={createPlan.error} />
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select className="w-auto min-w-44" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">Apply a template…</option>
          {templates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.name} ({tpl.items.length})
            </option>
          ))}
        </Select>
        <Button variant="secondary" disabled={!templateId || applyTemplate.isPending} onClick={() => applyTemplate.mutate()}>
          Apply
        </Button>
        {applyTemplate.isSuccess && <span className="text-xs text-emerald-700">Applied ✓</span>}
      </div>
      <ErrorText error={applyTemplate.error} />

      {plans.isLoading ? (
        <Spinner />
      ) : plans.error ? (
        <ErrorText error={plans.error} />
      ) : !current ? (
        <EmptyState title="No weekly plan yet" hint="Build one above or apply a template — items are auto-transposed to this student's range." />
      ) : (
        <div className="space-y-2">
          <div className="rounded-lg border border-brand-200 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-800">{current.title || 'Weekly plan'}</p>
              <span className="text-xs text-slate-400">week of {fmtDate(current.weekStart)}</span>
            </div>
            <div className="space-y-1.5">
              {current.items.map((item) => (
                <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-slate-700">{exName(item.exerciseId)}</p>
                    <p className="text-[11px] text-slate-400">
                      {item.frequencyPerWeek}× / week
                      {item.targetDays && ` · ${item.targetDays}`} · {item.tempoBpm} BPM
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      className="rounded-lg border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
                      onClick={() => patchItem.mutate({ itemId: item.id, rootMidi: item.rootMidi - 1 })}
                      disabled={patchItem.isPending}
                      aria-label="Transpose down a semitone"
                    >
                      −
                    </button>
                    <Tag tone={item.rootMidiOverride ? 'amber' : 'brand'}>root {midiToName(item.rootMidi)}</Tag>
                    <button
                      className="rounded-lg border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
                      onClick={() => patchItem.mutate({ itemId: item.id, rootMidi: item.rootMidi + 1 })}
                      disabled={patchItem.isPending}
                      aria-label="Transpose up a semitone"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <ErrorText error={patchItem.error} />
          </div>

          {past.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs font-medium text-slate-400">
                {past.length} past plan{past.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-2 space-y-1">
                {past.map((plan) => (
                  <div key={plan.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-xs text-slate-500">
                    <span className="truncate">{plan.title || 'Weekly plan'}</span>
                    <span className="shrink-0">
                      {fmtDate(plan.weekStart)} · {plan.items.length} items
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}

// ── C7 · Warm-up sequence ────────────────────────────────────────────────────

function WarmupCard({
  studentId,
  warmup,
  exercises,
  exName,
}: {
  studentId: string;
  warmup: { isLoading: boolean; error: unknown; data: Plan[] | undefined };
  exercises: Exercise[];
  exName: (id: string) => string;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [addId, setAddId] = useState('');

  const current = warmup.data?.[0];

  const save = useMutation({
    mutationFn: () =>
      api(`/api/warmup/${studentId}`, {
        method: 'PUT',
        body: { items: draft.map((exerciseId) => ({ exerciseId, frequencyPerWeek: 7 })) },
      }),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['plans', studentId] });
    },
  });

  return (
    <Card
      title="Warm-up sequence"
      actions={
        <Button
          variant="secondary"
          onClick={() => {
            if (!editing) setDraft(current?.items.map((i) => i.exerciseId) ?? []);
            setEditing((e) => !e);
          }}
        >
          {editing ? 'Cancel' : current ? 'Edit' : 'Set up'}
        </Button>
      }
    >
      {warmup.isLoading ? (
        <Spinner />
      ) : editing ? (
        <div className="space-y-2">
          {draft.length === 0 && <p className="text-xs text-slate-400">Add exercises to build the warm-up.</p>}
          {draft.map((exerciseId, i) => (
            <div key={`${exerciseId}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5">
              <p className="min-w-0 truncate text-sm text-slate-700">
                {i + 1}. {exName(exerciseId)}
              </p>
              <button
                className="shrink-0 rounded px-1.5 text-red-500 hover:bg-red-50"
                onClick={() => setDraft((prev) => prev.filter((_, idx) => idx !== i))}
                aria-label="Remove"
              >
                ✕
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <Select value={addId} onChange={(e) => setAddId(e.target.value)}>
              <option value="">Add an exercise…</option>
              {exercises.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {exLabel(ex)}
                </option>
              ))}
            </Select>
            <Button
              variant="secondary"
              className="shrink-0"
              disabled={!addId}
              onClick={() => {
                setDraft((prev) => [...prev, addId]);
                setAddId('');
              }}
            >
              Add
            </Button>
          </div>
          <Button onClick={() => save.mutate()} disabled={draft.length === 0 || save.isPending}>
            Save warm-up
          </Button>
          <ErrorText error={save.error} />
        </div>
      ) : !current || current.items.length === 0 ? (
        <p className="text-xs text-slate-400">
          No warm-up sequence yet — students can run it any time, outside the weekly plan.
        </p>
      ) : (
        <ol className="space-y-1">
          {current.items.map((item, i) => (
            <li key={item.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
              <span className="min-w-0 truncate">
                {i + 1}. {exName(item.exerciseId)}
              </span>
              <span className="shrink-0 text-xs text-slate-400">root {midiToName(item.rootMidi)}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
